import { v4 as uuidv4 } from "uuid";

interface GatekeeperOAuthClientConfig {
  clientId: string;
  redirectUri: string;
}

interface StoreGatekeeperOAuthStateParams {
  state: string;
  redirect_uri: string;
  client_id: string;
}

export type StoreGatekeeperOAuthState = (
  params: StoreGatekeeperOAuthStateParams
) => Promise<void>;

interface RedirectToAuthParams {
  gatekeeperLoginUrl: string;
  storeOAuthState: StoreGatekeeperOAuthState;
}

interface RedirectToAuthWithEnvParams {
  gatekeeperBaseUrl: string;
  organizationSlug: string;
  storeOAuthState: StoreGatekeeperOAuthState;
}

interface GatekeeperOAuthCallbackParams {
  code: string;
  state: string;
  redirect_uri: string;
  client_id: string;
}

export type GatekeeperOAuthCallbackHandler = (
  params: GatekeeperOAuthCallbackParams
) => Promise<any>;

/**
 * Reusable OAuth client for Gatekeeper OAuth integration (React version)
 * Adapted from the Next.js version in @dreambigger/shared
 *
 * Usage:
 * ```typescript
 * import { GatekeeperOAuthClient } from "./utils/gatekeeper-oauth-client";
 *
 * const oAuthClient = new GatekeeperOAuthClient({
 *   clientId: process.env.REACT_APP_GATEKEEPER_OAUTH_CLIENT_ID!,
 *   redirectUri: `${window.location.origin}/organization/${orgSlug}/oauth-gatekeeper-callback`,
 * });
 *
 * // In login page:
 * await oAuthClient.redirectToAuth({
 *   gatekeeperLoginUrl: `${gatekeeperUrl}/organization/${orgSlug}`,
 *   storeOAuthState: async (params) => {
 *     // Your API call
 *   }
 * });
 *
 * // In callback page:
 * const result = await oAuthClient.handleCallback(exchangeTokenCallback);
 * ```
 */
export class GatekeeperOAuthClient {
  private readonly config: GatekeeperOAuthClientConfig;

  constructor(config: GatekeeperOAuthClientConfig) {
    if (!config.clientId) {
      throw new Error("OAuth client ID is required");
    }
    if (!config.redirectUri) {
      throw new Error("Redirect URI is required");
    }

    this.config = config;
  }

  /**
   * Generate OAuth authorization URL and redirect user to Gatekeeper auth service
   */
  async redirectToAuth({
    gatekeeperLoginUrl,
    storeOAuthState,
  }: RedirectToAuthParams): Promise<void> {
    const state = this.generateState();

    // Store state via app-specific callback
    await storeOAuthState({
      state,
      redirect_uri: this.config.redirectUri,
      client_id: this.config.clientId,
    });

    // Build auth URL and redirect
    const params = new URLSearchParams({
      response_type: "code",
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      state,
    });

    const authUrl = `${gatekeeperLoginUrl}?${params.toString()}`;
    window.location.href = authUrl;
  }

  /**
   * Enhanced redirect method that handles environment variables and validation
   */
  async redirectToAuthWithEnv({
    gatekeeperBaseUrl,
    organizationSlug,
    storeOAuthState,
  }: RedirectToAuthWithEnvParams): Promise<void> {
    // Validate required parameters
    if (!gatekeeperBaseUrl) {
      throw new Error("Gatekeeper base URL is required but not provided");
    }
    if (!organizationSlug) {
      throw new Error("Organization slug is required but not provided");
    }

    // Build the full gatekeeper login URL
    const gatekeeperLoginUrl = `${gatekeeperBaseUrl}/organization/${organizationSlug}`;

    // Use the existing redirectToAuth method
    await this.redirectToAuth({
      gatekeeperLoginUrl,
      storeOAuthState,
    });
  }

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  async handleCallback(
    onOAuthCallback: GatekeeperOAuthCallbackHandler
  ): Promise<any> {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const error = urlParams.get("error");

    if (error) {
      throw new Error(
        `OAuth error: ${error} - ${urlParams.get("error_description")}`
      );
    }

    if (!code || !state) {
      throw new Error("Missing authorization code or state parameter");
    }

    // Exchange code for tokens via app-specific callback
    const result = await onOAuthCallback({
      code,
      state,
      redirect_uri: this.config.redirectUri,
      client_id: this.config.clientId,
    });

    return result;
  }

  /**
   * Check if current URL is an OAuth callback
   */
  isCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has("code") || urlParams.has("error");
  }

  /**
   * Generate a random state parameter
   */
  private generateState(): string {
    return uuidv4();
  }
}
